import React, { useState } from 'react';
import { IHSANROLL_PROGRAMME } from '../../data/ihsanRollMock';
import {
  attendanceKey,
  attendanceCompleteness,
  editMovement,
  openOutFor,
  editsFor,
  formatDateLabel,
  formatTime,
  isOverdue,
  last7Days,
  movementsOnDate,
  saveAttendance,
  todayISO
} from '../../lib/ihsanRollStore';
import { fieldClass, StatusDot } from './shared';

function EditedMarker({ store, target }) {
  const history = editsFor(store, target);
  const [open, setOpen] = useState(false);
  if (!history.length) return null;
  return (
    <div className="mt-1">
      <button
        type="button"
        className="text-[11px] font-bold text-amber-700"
        onClick={() => setOpen((v) => !v)}
      >
        Edited
      </button>
      {open && (
        <ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">
          {history.map((e) => (
            <li key={`${e.at}-${e.field}`}>
              {e.field}: {String(e.from || '—')} → {String(e.to || '—')} · {e.by} · {formatTime(e.at.slice(11, 16))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function StatusView({ store, caseNo, update }) {
  const [mode, setMode] = useState('day');
  const [date, setDate] = useState(todayISO);
  const officer = store.dutyOfficer;
  const sheet = store.attendance[attendanceKey(caseNo, date)];
  const moves = movementsOnDate(store, caseNo, date);
  const week = last7Days();
  const open = openOutFor(store, caseNo);

  const patchRemark = (itemId, remarks) => {
    if (!officer || !sheet) return;
    const items = {
      ...sheet.items,
      [itemId]: { ...sheet.items[itemId], remarks }
    };
    update((prev) => saveAttendance(prev, caseNo, date, items, officer));
  };

  const patchMoveTime = (id, time) => {
    if (!officer) return;
    update((prev) => editMovement(prev, id, 'time', time, officer));
  };

  return (
    <div className="px-4 pt-5 pb-8" data-testid="ihsanroll-status">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Case #{caseNo}
      </p>
      <h1 className="text-2xl font-extrabold text-[#0F172A] mt-1">Status</h1>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {['day', 'week'].map((id) => (
          <button
            key={id}
            type="button"
            data-testid={`ihsanroll-status-${id}`}
            aria-pressed={mode === id}
            onClick={() => setMode(id)}
            className={`h-11 rounded-xl text-sm font-semibold capitalize ${
              mode === id ? 'bg-primary text-white' : 'bg-muted/60 text-[#0F172A]'
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {mode === 'day' && (
        <div className="mt-4 md:grid md:grid-cols-2 md:gap-6">
          <div>
            <label className="text-xs font-semibold text-[#0F172A]" htmlFor="status-date">Date</label>
            <input
              id="status-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={fieldClass}
            />
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Attendance</p>
            {!sheet && (
              <p className="mt-2 text-sm text-muted-foreground">Attendance not yet logged for this date.</p>
            )}
            {sheet && (
              <div className="mt-2 flex flex-col gap-2">
                {IHSANROLL_PROGRAMME.map((p) => {
                  const row = sheet.items[p.id] || {};
                  return (
                    <div key={p.id} className="rounded-xl border border-border bg-white p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-[#0F172A]">{p.label}</p>
                        <span className={`text-[11px] font-bold capitalize ${row.status === 'absent' ? 'text-[#D64545]' : 'text-primary'}`}>
                          {row.status || 'unset'}
                        </span>
                      </div>
                      <input
                        type="text"
                        aria-label={`Edit remarks for ${p.label}`}
                        defaultValue={row.remarks || ''}
                        key={`${p.id}-${sheet.savedAt}-${row.remarks || ''}`}
                        onBlur={(e) => {
                          if ((row.remarks || '') !== e.target.value) patchRemark(p.id, e.target.value);
                        }}
                        className={`${fieldClass} h-9 text-xs`}
                      />
                      <p className="mt-1 text-[10px] text-muted-foreground">{sheet.savedBy}</p>
                      <EditedMarker store={store} target={`attendance:${attendanceKey(caseNo, date)}:${p.id}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-6 md:mt-0">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Movement</p>
            {!moves.length && (
              <p className="mt-2 text-sm text-muted-foreground">No movement logged this date.</p>
            )}
            <div className="mt-2 flex flex-col gap-2">
              {moves.map((entry) => {
                const overdue = entry.type === 'out' && open?.id === entry.id && isOverdue(entry);
                return (
                  <div
                    key={entry.id}
                    className={`rounded-xl border p-3 ${overdue ? 'border-[#D64545]/40 bg-red-50' : 'border-border bg-white'}`}
                  >
                    <p className={`text-sm font-semibold ${overdue ? 'text-[#D64545]' : 'text-[#0F172A]'}`}>
                      {entry.type === 'out' ? 'Out' : 'In'} · {entry.place || '—'}
                    </p>
                    <label className="mt-2 block text-[11px] text-muted-foreground">
                      Time
                      <input
                        type="time"
                        defaultValue={entry.time}
                        key={`${entry.id}-${entry.time}`}
                        onBlur={(e) => {
                          if (e.target.value && e.target.value !== entry.time) {
                            patchMoveTime(entry.id, e.target.value);
                          }
                        }}
                        className={`${fieldClass} h-9`}
                      />
                    </label>
                    <p className="mt-1 text-[10px] text-muted-foreground">{entry.officer}</p>
                    <EditedMarker store={store} target={`movement:${entry.id}`} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {mode === 'week' && (
        <div className="mt-4 grid grid-cols-1 gap-2" data-testid="ihsanroll-week">
          {week.map((iso) => {
            const completeness = attendanceCompleteness(store, caseNo, iso);
            const dayMoves = movementsOnDate(store, caseNo, iso);
            const overdueDay = Boolean(
              open && isOverdue(open) && todayISO(new Date(open.createdAt)) === iso
            );
            return (
              <button
                key={iso}
                type="button"
                onClick={() => { setDate(iso); setMode('day'); }}
                className={`rounded-xl border p-3 text-left ${overdueDay ? 'border-[#D64545]/40 bg-red-50' : 'border-border bg-white'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-[#0F172A]">{formatDateLabel(iso)}</span>
                  {overdueDay && <StatusDot status="overdue" />}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Attendance: {completeness === 'logged' ? 'logged' : completeness === 'partial' ? 'partial' : 'not logged'}
                  {' · '}
                  Movements: {dayMoves.length}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
